/**
 * PartLookupPicker
 *
 * Reusable parts search + multi-select component for Wadakaroo.
 * Zero Redux dependency — calls GET /api/repair-parts via axiosInstance directly.
 *
 * ⚠️ /repair-parts, not /parts. See the note at the top of services/partsApi.js: /parts
 * was the marketplace listing API, and this picker renders RepairPart fields.
 *
 * Props
 * ─────
 * selectedUuids   {string[]}       Controlled selection (UUIDs)
 * selectedParts   {PartResponse[]} Full objects for current selection (for pill rendering)
 * onChange        (uuids: string[], parts: PartResponse[]) => void
 * label           {string}         Section heading  (default: "Parts")
 * maxHeight       {string}         Tailwind max-h class for results list (default: "max-h-56")
 *
 * Search field layout
 * ───────────────────
 *   lg+  → single row  (4 fields + Search button all inline)
 *   md   → 2 × 2 grid  + button row below
 *   sm   → 4 stacked   + button row below
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import axiosInstance from '../../services/axiosInstance';

// ─── Constants ────────────────────────────────────────────────────────────────

const EMPTY_FILTERS = { make: '', model: '', name: '', manufacturerPartId: '' };
const PAGE_SIZE = 20;

// ─── Helpers ──────────────────────────────────────────────────────────────────

const buildSearchParams = ({ make, model, name, manufacturerPartId, page, size }) => {
    const p = new URLSearchParams({ page: String(page), size: String(size), activeOnly: 'true' });
    if (make?.trim())               p.set('make',               make.trim());
    if (model?.trim())              p.set('model',              model.trim());
    if (name?.trim())               p.set('name',               name.trim());
    if (manufacturerPartId?.trim()) p.set('manufacturerPartId', manufacturerPartId.trim());
    return p;
};

// ─── Small shared components ──────────────────────────────────────────────────

const Spinner = ({ size = 4 }) => (
    <div className={`animate-spin rounded-full w-${size} h-${size} border-2 border-gray-200 border-t-emerald-600`} />
);

const SearchField = ({ icon, placeholder, value, onChange, onKeyDown }) => (
    <div className="relative min-w-0">
        <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none text-gray-400">
            {icon}
        </div>
        <input
            type="text"
            value={value}
            onChange={e => onChange(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder={placeholder}
            className="w-full pl-8 pr-3 py-2 border border-gray-200 rounded-lg text-sm
                       focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500
                       outline-none bg-white placeholder-gray-400"
        />
    </div>
);

const PartPill = ({ part, onRemove }) => (
    <span className="inline-flex items-center gap-1.5 pl-2.5 pr-1.5 py-1
                     bg-emerald-50 text-emerald-800 border border-emerald-200
                     rounded-lg text-xs font-medium max-w-[220px]">
        <span className="truncate">{part.name}</span>
        {part.manufacturerPartId && (
            <span className="text-emerald-500 font-mono shrink-0 hidden sm:inline">
                {part.manufacturerPartId}
            </span>
        )}
        <button
            type="button"
            onClick={() => onRemove(part.uuid)}
            className="shrink-0 w-4 h-4 flex items-center justify-center rounded-full
                       hover:bg-emerald-200 transition-colors text-emerald-600 hover:text-emerald-800"
        >
            <svg className="w-2.5 h-2.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
            </svg>
        </button>
    </span>
);

// ─── Icons ────────────────────────────────────────────────────────────────────

const IconMake = () => (
    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
              d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
    </svg>
);
const IconModel = () => (
    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
              d="M9 17a2 2 0 11-4 0 2 2 0 014 0zM19 17a2 2 0 11-4 0 2 2 0 014 0z" />
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
              d="M13 16V6a1 1 0 00-1-1H4a1 1 0 00-1 1v10l2 1h8zm0 0l2 1h3a1 1 0 001-1V9a1 1 0 00-.293-.707l-3-3A1 1 0 0015 5h-2v11z" />
    </svg>
);
const IconTag = () => (
    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
              d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
    </svg>
);
const IconHash = () => (
    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 20l4-16m2 16l4-16M6 9h14M4 15h14" />
    </svg>
);
const IconSearch = () => (
    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
    </svg>
);

// ─── Main component ───────────────────────────────────────────────────────────

const PartLookupPicker = ({
                              selectedUuids  = [],
                              selectedParts: initialSelectedParts = [],
                              onChange,
                              label     = 'Parts',
                              maxHeight = 'max-h-56',
                          }) => {
    const [pending,       setPending]       = useState(EMPTY_FILTERS);
    const [committed,     setCommitted]     = useState(EMPTY_FILTERS);
    const [results,       setResults]       = useState([]);
    const [totalElements, setTotalElements] = useState(0);
    const [page,          setPage]          = useState(0);
    const [totalPages,    setTotalPages]    = useState(0);
    const [isLoading,     setIsLoading]     = useState(false);
    const [searchError,   setSearchError]   = useState(null);
    const [hasSearched,   setHasSearched]   = useState(false);

    // uuid → part object, survives pagination
    const [selectedMap, setSelectedMap] = useState(() => {
        const m = {};
        initialSelectedParts.forEach(p => { m[p.uuid] = p; });
        return m;
    });

    const prevKey = useRef(null);
    useEffect(() => {
        const key = selectedUuids.join(',');
        if (key === prevKey.current) return;
        prevKey.current = key;
        setSelectedMap(prev => {
            const next = { ...prev };
            initialSelectedParts.forEach(p => { if (!next[p.uuid]) next[p.uuid] = p; });
            return next;
        });
    }, [selectedUuids, initialSelectedParts]);

    const fetchPage = useCallback(async (filters, targetPage) => {
        setIsLoading(true);
        setSearchError(null);
        try {
            const params = buildSearchParams({ ...filters, page: targetPage, size: PAGE_SIZE });
            const { data } = await axiosInstance.get(`/repair-parts?${params}`);
            setResults(data.content ?? []);
            setTotalElements(data.totalElements ?? 0);
            setTotalPages(data.totalPages ?? 1);
            setPage(targetPage);
            setHasSearched(true);
            setSelectedMap(prev => {
                const next = { ...prev };
                (data.content ?? []).forEach(p => {
                    if (selectedUuids.includes(p.uuid)) next[p.uuid] = p;
                });
                return next;
            });
        } catch {
            setSearchError('Search failed — please try again.');
        } finally {
            setIsLoading(false);
        }
    }, [selectedUuids]);

    const handleSearch = () => { setCommitted(pending); fetchPage(pending, 0); };
    const handleKeyDown = e => { if (e.key === 'Enter') { e.preventDefault(); handleSearch(); } };
    const handleClear   = () => {
        setPending(EMPTY_FILTERS);
        setCommitted(EMPTY_FILTERS);
        setResults([]);
        setHasSearched(false);
        setTotalElements(0);
    };

    const togglePart = part => {
        const isSelected = selectedUuids.includes(part.uuid);
        const nextUuids = isSelected
            ? selectedUuids.filter(u => u !== part.uuid)
            : [...selectedUuids, part.uuid];
        const nextMap = { ...selectedMap };
        if (isSelected) delete nextMap[part.uuid];
        else nextMap[part.uuid] = part;
        setSelectedMap(nextMap);
        onChange(nextUuids, Object.values(nextMap));
    };

    const removePart = uuid => {
        const nextUuids = selectedUuids.filter(u => u !== uuid);
        const nextMap   = { ...selectedMap };
        delete nextMap[uuid];
        setSelectedMap(nextMap);
        onChange(nextUuids, Object.values(nextMap));
    };

    const selectedCount = selectedUuids.length;
    const hasFilters    = Object.values(pending).some(v => v.trim());

    return (
        <div className="space-y-3">
            {/* Label row */}
            <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-700">
                    {label}
                    {selectedCount > 0 && (
                        <span className="ml-1.5 text-xs font-normal text-gray-400">
                            ({selectedCount} selected)
                        </span>
                    )}
                </span>
                {selectedCount > 0 && (
                    <button type="button" onClick={() => { setSelectedMap({}); onChange([], []); }}
                            className="text-xs text-gray-400 hover:text-red-500 transition-colors">
                        Clear all
                    </button>
                )}
            </div>

            {/* Selected pills */}
            {selectedCount > 0 && (
                <div className="flex flex-wrap gap-1.5 p-2.5 bg-gray-50 border border-gray-200 rounded-xl">
                    {selectedUuids.map(uuid => {
                        const part = selectedMap[uuid];
                        return part
                            ? <PartPill key={uuid} part={part} onRemove={removePart} />
                            : (
                                <span key={uuid}
                                      className="inline-flex items-center px-2.5 py-1 bg-gray-100 text-gray-500
                                                 border border-gray-200 rounded-lg text-xs font-mono">
                                    {uuid.slice(0, 8)}…
                                </span>
                            );
                    })}
                </div>
            )}

            {/* ── Search panel ─────────────────────────────────────────────────
                Layout:
                  sm  (default) → 1 col stacked
                  md            → 2 × 2 grid
                  lg+           → all 4 fields + button in a single flex row
            ─────────────────────────────────────────────────────────────────── */}
            <div className="rounded-xl border border-gray-200 bg-gray-50 p-3">
                <div className="flex flex-col lg:flex-row gap-2">
                    {/* 4 search fields */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2 flex-1 min-w-0">
                        <SearchField icon={<IconMake />}  placeholder="Make (e.g. Toyota)"
                                     value={pending.make}               onChange={v => setPending(p => ({ ...p, make: v }))}
                                     onKeyDown={handleKeyDown} />
                        <SearchField icon={<IconModel />} placeholder="Model (e.g. Camry)"
                                     value={pending.model}              onChange={v => setPending(p => ({ ...p, model: v }))}
                                     onKeyDown={handleKeyDown} />
                        <SearchField icon={<IconTag />}   placeholder="Part name"
                                     value={pending.name}               onChange={v => setPending(p => ({ ...p, name: v }))}
                                     onKeyDown={handleKeyDown} />
                        <SearchField icon={<IconHash />}  placeholder="Mfr part ID"
                                     value={pending.manufacturerPartId} onChange={v => setPending(p => ({ ...p, manufacturerPartId: v }))}
                                     onKeyDown={handleKeyDown} />
                    </div>

                    {/* Buttons — inline on lg, full-width row below on sm/md */}
                    <div className="flex gap-2 lg:shrink-0">
                        <button
                            type="button"
                            onClick={handleSearch}
                            disabled={isLoading}
                            className="flex-1 lg:flex-none inline-flex items-center justify-center gap-1.5
                                       px-4 py-2 bg-emerald-600 text-white text-sm font-semibold rounded-lg
                                       hover:bg-emerald-700 disabled:opacity-60 disabled:cursor-not-allowed
                                       transition-colors whitespace-nowrap"
                        >
                            {isLoading ? <Spinner size={3} /> : <IconSearch />}
                            {isLoading ? 'Searching…' : 'Search'}
                        </button>
                        {(hasFilters || hasSearched) && (
                            <button
                                type="button"
                                onClick={handleClear}
                                className="px-3 py-2 text-sm text-gray-500 hover:text-gray-700
                                           border border-gray-200 rounded-lg hover:bg-white transition-colors"
                            >
                                Clear
                            </button>
                        )}
                    </div>
                </div>
            </div>

            {/* Error */}
            {searchError && <p className="text-xs text-red-600 px-1">{searchError}</p>}

            {/* ── Results ── */}
            {hasSearched && !isLoading && (
                <>
                    <div className="flex items-center justify-between px-0.5">
                        <p className="text-xs text-gray-400">
                            {totalElements === 0
                                ? 'No parts found — try different terms'
                                : `${totalElements} part${totalElements !== 1 ? 's' : ''} found`}
                        </p>
                        {totalPages > 1 && (
                            <p className="text-xs text-gray-400">Page {page + 1} of {totalPages}</p>
                        )}
                    </div>

                    {results.length > 0 && (
                        <>
                            <div className={`${maxHeight} overflow-y-auto border border-gray-200
                                            rounded-xl divide-y divide-gray-100 bg-white`}>
                                {results.map(part => {
                                    const isSelected = selectedUuids.includes(part.uuid);
                                    return (
                                        <label key={part.uuid}
                                               className={`flex items-center gap-3 px-3.5 py-2.5 cursor-pointer transition-colors ${
                                                   isSelected ? 'bg-emerald-50' : 'hover:bg-gray-50'
                                               }`}>
                                            <input
                                                type="checkbox"
                                                checked={isSelected}
                                                onChange={() => togglePart(part)}
                                                className="w-4 h-4 text-emerald-600 border-gray-300 rounded
                                                           focus:ring-emerald-500 shrink-0"
                                            />
                                            <div className="flex-1 min-w-0">
                                                <p className={`text-sm truncate ${
                                                    isSelected ? 'text-emerald-800 font-medium' : 'text-gray-800'
                                                }`}>
                                                    {part.name}
                                                </p>
                                                {(part.make || part.model) && (
                                                    <p className="text-xs text-gray-400 truncate">
                                                        {[part.make, part.model].filter(Boolean).join(' · ')}
                                                    </p>
                                                )}
                                            </div>
                                            <div className="shrink-0 flex flex-col items-end gap-0.5">
                                                {part.manufacturerPartId && (
                                                    <span className="text-xs font-mono text-gray-400">
                                                        {part.manufacturerPartId}
                                                    </span>
                                                )}
                                                {part.cost != null && (
                                                    <span className={`text-xs font-semibold ${
                                                        isSelected ? 'text-emerald-700' : 'text-gray-600'
                                                    }`}>
                                                        ${Number(part.cost).toFixed(2)}
                                                    </span>
                                                )}
                                            </div>
                                        </label>
                                    );
                                })}
                            </div>

                            {totalPages > 1 && (
                                <div className="flex items-center justify-end gap-2">
                                    <button type="button"
                                            onClick={() => fetchPage(committed, page - 1)}
                                            disabled={page === 0 || isLoading}
                                            className="px-3 py-1.5 text-xs rounded-lg border border-gray-200
                                                       hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed
                                                       transition-colors">
                                        Previous
                                    </button>
                                    <button type="button"
                                            onClick={() => fetchPage(committed, page + 1)}
                                            disabled={page >= totalPages - 1 || isLoading}
                                            className="px-3 py-1.5 text-xs rounded-lg border border-gray-200
                                                       hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed
                                                       transition-colors">
                                        Next
                                    </button>
                                </div>
                            )}
                        </>
                    )}
                </>
            )}
        </div>
    );
};

export default PartLookupPicker;